import { Injectable } from '@angular/core';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

@Injectable({
  providedIn: 'root',
})
export class AuthSessionCacheService {
  private readonly cacheTtlMs = 60 * 1000;
  private cachedSession: any = null;
  private cachedAt = 0;
  private inFlightSessionPromise: Promise<any> | null = null;

  private async delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async fetchSessionWithRetry(forceRefresh: boolean): Promise<any> {
    const retryDelaysMs = [250, 500, 1000, 2000];
    for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
      try {
        return await fetchAuthSession({ forceRefresh });
      } catch (error) {
        const isTooManyRequests =
          error?.name === 'TooManyRequestsException' || error?.__type === 'TooManyRequestsException';
        if (!isTooManyRequests || attempt === retryDelaysMs.length) {
          throw error;
        }
        await this.delay(retryDelaysMs[attempt]);
      }
    }
    return null;
  }

  async getSession(forceRefresh = false): Promise<any> {
    if (!forceRefresh && this.cachedSession && Date.now() - this.cachedAt < this.cacheTtlMs) {
      return this.cachedSession;
    }

    if (this.inFlightSessionPromise) {
      return this.inFlightSessionPromise;
    }

    this.inFlightSessionPromise = (async () => {
      await getCurrentUser();
      const session = await this.fetchSessionWithRetry(forceRefresh);

      if (session && !forceRefresh) {
        this.cachedSession = session;
        this.cachedAt = Date.now();
      }

      return session;
    })();

    try {
      return await this.inFlightSessionPromise;
    } finally {
      this.inFlightSessionPromise = null;
    }
  }

  async getAccessToken(forceRefresh = false): Promise<string> {
    const session = await this.getSession(forceRefresh);
    return session?.tokens?.accessToken?.toString() || null;
  }

  async getCredentials(forceRefresh = false): Promise<any> {
    const session = await this.getSession(forceRefresh);
    return session?.credentials || null;
  }

  clearCache(): void {
    this.cachedSession = null;
    this.cachedAt = 0;
    this.inFlightSessionPromise = null;
  }
}
